import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, X } from 'lucide-react';
import { version as APP_VERSION } from '../../package.json';
import { ChangelogInhalt, useChangelog } from './ChangelogAnsicht';

/**
 * Welche Fassung hier laeuft — unten in der Seitenleiste.
 *
 * ══ ZWEI TEILE, ZWEI AUFGABEN ══════════════════════════════════════════════
 *
 * Vorn die gepflegte Nummer aus der package.json. Sie sagt, welcher Stand
 * gemeint ist, und waechst mit jeder Aenderung — siehe die Regel in CLAUDE.md.
 *
 * Dahinter die Baukennung: Datum und Commit, von Vite beim Bauen eingesetzt.
 * Sie sagt, welcher Stand tatsaechlich laeuft. Das ist der Teil, der im Betrieb
 * zaehlt — und der einzige, der nicht daran haengt, dass jemand daran gedacht
 * hat. Steht dort `dev`, wurde ohne Git gebaut.
 *
 * ══ WARUM DIE NUMMER ANKLICKBAR IST ════════════════════════════════════════
 *
 * Weil sie meistens weitergegeben wird: in einen Kanal, in ein Ticket, an
 * jemanden, der fragt „welche Fassung?". Abtippen erzeugt Zahlendreher, und ein
 * Zahlendreher in einer Versionsnummer schickt die Fehlersuche in den falschen
 * Stand.
 *
 * Gleiches Verhalten wie in ojfm — dieselbe Stelle, derselbe Klick, dasselbe
 * Info-Zeichen daneben. Wer beide Anwendungen benutzt, soll nicht umlernen.
 */
export default function SidebarVersion() {
  const { t } = useTranslation();
  const [kopiert, setKopiert] = useState(false);
  const [offen, setOffen] = useState(false);

  const version = APP_VERSION || '';
  const bau = typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : '';
  const zumKopieren = bau ? `v${version} · ${bau}` : `v${version}`;

  if (!version) return null;

  async function kopieren() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(zumKopieren);
      } else {
        // Ohne sicheren Kontext gibt es die Zwischenablage nicht. Der alte Weg
        // ueber ein verstecktes Feld funktioniert dort weiterhin.
        const feld = document.createElement('textarea');
        feld.value = zumKopieren;
        feld.style.position = 'fixed';
        feld.style.opacity = '0';
        document.body.appendChild(feld);
        feld.select();
        document.execCommand('copy');
        document.body.removeChild(feld);
      }
      setKopiert(true);
      setTimeout(() => setKopiert(false), 1600);
    } catch {
      // Keine Zwischenablage, keine Meldung: die Nummer steht sichtbar da und
      // laesst sich von Hand markieren. Ein Fehlerhinweis waere hier Laerm.
    }
  }

  return (
    <>
      <div className="flex items-center justify-between gap-1 border-t border-slate-700/50 px-4 py-2">
        <button
          type="button"
          onClick={kopieren}
          title={t('version.copyHint')}
          aria-label={t('version.copyHint')}
          className="truncate rounded px-1 py-0.5 text-left font-mono text-[11px] text-slate-500 transition-colors hover:text-slate-300"
        >
          {kopiert ? (
            <span className="text-emerald-400">✓ {t('version.copied')}</span>
          ) : (
            <>
              v{version}
              {bau && <span className="ml-1.5 text-slate-600">· {bau}</span>}
            </>
          )}
        </button>

        <button
          type="button"
          onClick={() => setOffen(true)}
          title={t('version.whatsNew')}
          aria-label={t('version.whatsNew')}
          className="shrink-0 rounded p-1 text-slate-600 transition-colors hover:text-slate-300"
        >
          <Info size={13} />
        </button>
      </div>

      {offen && <ChangelogFenster version={version} onSchliessen={() => setOffen(false)} />}
    </>
  );
}

function ChangelogFenster({
  version,
  onSchliessen,
}: {
  version: string;
  onSchliessen: () => void;
}) {
  const { t } = useTranslation();
  const { text, laedt } = useChangelog();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onSchliessen}
      role="presentation"
    >
      <div
        className="max-h-[80vh] w-full max-w-2xl overflow-auto rounded-xl bg-surface p-5 shadow-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('version.whatsNew')}
      >
        <div className="mb-3 flex items-start justify-between gap-4">
          <h2 className="text-base font-semibold text-slate-800">
            {t('version.whatsNewWithVersion', { version })}
          </h2>
          <button
            type="button"
            onClick={onSchliessen}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label={t('common.close')}
          >
            <X size={16} />
          </button>
        </div>

        {laedt ? (
          <div className="h-24 animate-pulse rounded bg-slate-100" />
        ) : text ? (
          <ChangelogInhalt text={text} />
        ) : (
          <p className="text-sm text-slate-500">{t('version.changelogEmpty')}</p>
        )}
      </div>
    </div>
  );
}
